import { TimesOutlineIcon } from '@ikyucom/design-system-icons-react'
import { useCallback, useRef } from 'react'
import type { MouseEvent } from 'react'
import Image from '~/components/image/Image'
import { FusumaTitle } from '~/components/modal/Fusuma'
import { KiwaminoArticleInfo } from '~/features/guide/KiwaminoArticles'
import { dateTimeToDateText } from '~/lib/date/dateTime'
import { type FragmentType, getFragmentData } from '~/lib/graphql'
import type { DateTime, KiwaminoArticleId } from '~/lib/graphql/scalars'
import { useIsPC } from '~/lib/ua'
import { css, sva } from '~/styled-system/css'
import { Button } from '~/ui'
import { closeDialogAfterAnimation } from './lib/animation'

const dialogStyles = sva({
  slots: ['overlay', 'container', 'title', 'close', 'closeIcon', 'section', 'button'],
  base: {
    overlay: {
      position: 'fixed',
      inset: '0',
      // 下部フロートや「もっとみる」ボタンが z-index で管理されているので、それよりも上に表示するために z-index を設定
      zIndex: '10',
      height: '[100dvh]',
      width: '[100dvw]',
      overflowY: 'scroll',
      overscrollBehavior: 'contain',
      _pc: {
        backgroundColor: '[rgba(0, 0, 0, 0.35)]',
      },
    },
    container: {
      _pc: {
        height: '[100dvh]',
        width: '[850px]',
        overflowY: 'scroll',
        overscrollBehavior: 'contain',
        position: 'absolute',
        right: '0',
        backgroundColor: 'white',
        p: '8',
        fontSize: 'md',
        spaceY: '5',
      },
    },
    title: {
      position: 'sticky',
      top: '0',
      outline: 'none',
    },
    section: {
      spaceY: '3',
      paddingX: '3',
      paddingBottom: '4',
      paddingTop: '5',
      _pc: {
        p: '0',
        spaceY: '0',
      },
    },
    button: {
      _sd: {
        padding: '3',
      },
    },
    close: {
      display: 'none',
      _pc: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        top: '0',
        right: '[850px]',
      },
    },
    closeIcon: {
      h: '[48px]',
      w: '[48px]',
      color: 'white',
    },
  },
})

type Props = {
  articleId: KiwaminoArticleId
  articles: FragmentType<typeof KiwaminoArticleInfo>[]
  switchArticle: (articleId: KiwaminoArticleId) => Promise<void>
}

function KiwaminoArticleDialog({ articleId, articles, switchArticle }: Props) {
  const currentArticleFragment = articles.find((fragment) => {
    const article = getFragmentData(KiwaminoArticleInfo, fragment)
    return article.articleId === articleId
  })
  const articleRef = useRef<HTMLDialogElement>(null)
  const contentsRef = useRef<HTMLDivElement>(null)
  const isPc = useIsPC()

  const closeArticle = useCallback(async () => {
    const dialog = articleRef.current
    const contents = contentsRef.current
    if (dialog === null || contents === null) {
      return
    }
    await closeDialogAfterAnimation(dialog, isPc)
  }, [isPc])

  const onClickOverlay = useCallback(
    async (e: MouseEvent<HTMLDialogElement>) => {
      const dialog = articleRef.current
      const contents = contentsRef.current
      // イベントの発生源が overlay のときだけ閉じる
      if (e.currentTarget === e.target && e.target === dialog && contents !== null) {
        await closeDialogAfterAnimation(dialog, isPc)
      }
    },
    [isPc]
  )

  const classes = dialogStyles()
  return (
    <dialog
      data-article-id={articleId}
      className={classes.overlay}
      ref={articleRef}
      onClick={onClickOverlay}
    >
      <div data-article-id={articleId}>
        <button type="button" className={classes.close} onClick={closeArticle}>
          <TimesOutlineIcon className={classes.closeIcon} />
        </button>
        <div className={classes.container} ref={contentsRef}>
          {/* dialog要素はロード時に内部のフォーカス可能要素に対して勝手にフォーカスを当ててしまう */}
          {/* そのため、FusumaTitle中の「戻る」ボタンにフォーカスが当たって青いoutlineが適用されてしまう */}
          {/* FusumaTitleを囲むdiv要素を身代わりのフォーカス対象にして、focus:outline-noneでフォーカス時のスタイルを当てないようにした */}
          {/* これにより、ロード時にどの要素にもフォーカスによる青いoutlineが適用されないようにした */}
          <div className={classes.title} tabIndex={0}>
            <FusumaTitle title="" close={closeArticle} headerColor="neutral" />
          </div>
          <div className={classes.section}>
            <KiwaminoDialogContent currentArticle={currentArticleFragment} />
          </div>
          <RelatedArticles
            articleId={articleId}
            articles={articles}
            switchArticle={switchArticle}
          />
          <div className={classes.button}>
            <Button size="lg" color="tertiary" spread={true} onClick={closeArticle} asChild={false}>
              店舗情報をみる
            </Button>
          </div>
        </div>
      </div>
    </dialog>
  )
}

function KiwaminoDialogContent({
  currentArticle,
}: {
  currentArticle: FragmentType<typeof KiwaminoArticleInfo> | undefined
}) {
  if (currentArticle === undefined) {
    return null
  }
  const { title, content, postedAt, thumbnailUrl } = getFragmentData(
    KiwaminoArticleInfo,
    currentArticle
  )
  const { dateText } = dateTimeToDateText(postedAt as DateTime)
  return (
    <div className={css({ spaceY: '4', _pc: { spaceY: '5' } })}>
      <div className={css({ spaceY: '2', _pc: { spaceY: '3' } })}>
        <h1 className={css({ fontWeight: 'bold', fontSize: '2xl', _pc: { fontSize: '3xl' } })}>
          {title}
        </h1>
        <p className={css({ color: 'gray.700' })}>{dateText} 更新</p>
      </div>
      <Image src={thumbnailUrl} alt={title} className={css({ w: 'full' })} usePlaceholder />
      <div dangerouslySetInnerHTML={{ __html: content as string }} />
    </div>
  )
}

const bodyStyles = sva({
  slots: ['body', 'title', 'contents', 'button', 'image', 'articleItem', 'articleTitle', 'chevron'],
  base: {
    body: { width: '[100%]' },
    title: {
      fontSize: 'lg',
      fontWeight: 'bold',
      _pc: {
        fontSize: '3xl',
      },
      _sd: {
        px: '3',
      },
    },
    contents: {
      display: 'flex',
      alignItems: 'center',
      gap: '2',
      overflowX: 'auto',
      paddingBottom: '2',
      paddingRight: '3',
      paddingTop: '3',
      _pc: {
        pr: '0',
        position: 'relative',
      },
    },
    button: {
      display: 'flex',
      width: '[18rem]',
      flexShrink: 0,
      flexDirection: 'row',
      gap: '2',
      borderRadius: 'md',
      backgroundColor: 'white',
      _sd: {
        '&:first-of-type': { marginLeft: '3' },
        alignItems: 'center',
        boxShadow: 'outline.sm',
      },
    },
    image: {
      borderRadius: '[4px 0 0 4px]',
      objectFit: 'cover',
      _pc: {
        borderRadius: 'md',
      },
    },
    articleItem: {
      display: 'flex',
      flex: '1',
      alignItems: 'center',
      paddingRight: '3',
      textAlign: 'left',
    },
    articleTitle: {
      display: '-webkit-box',
      fontSize: 'sm',
      lineClamp: 2,
      _pc: {
        color: 'link',
        lineClamp: 3,
      },
    },
  },
})

function RelatedArticles({
  articleId,
  articles: fragment,
  switchArticle,
}: {
  articleId: KiwaminoArticleId
  articles: FragmentType<typeof KiwaminoArticleInfo>[]
  switchArticle: (articleId: KiwaminoArticleId) => Promise<void>
}) {
  const articles = getFragmentData(KiwaminoArticleInfo, fragment)
  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const articleId = e.currentTarget.dataset.articleId as KiwaminoArticleId
      switchArticle(articleId)
    },
    [switchArticle]
  )

  const classes = bodyStyles()
  return (
    <div className={classes.body}>
      <h3 className={classes.title}>このレストランの記事</h3>
      <div className={classes.contents}>
        {articles
          .filter((article) => article.articleId !== articleId)
          .map((article) => (
            <button
              data-article-id={article.articleId}
              type="button"
              onClick={onClick}
              key={article.articleId}
              className={classes.button}
            >
              <Image
                src={article.thumbnailUrl}
                alt={article.title}
                className={classes.image}
                width={75}
                height={75}
                usePlaceholder={true}
              />
              <div className={classes.articleItem}>
                <p className={classes.articleTitle}>{article.title}</p>
              </div>
            </button>
          ))}
      </div>
    </div>
  )
}

export { KiwaminoArticleDialog }
